import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BlobHandler, { BLOB_STORAGE_CONSTANTS } from '@/user-agreement/services/BlobHandler';
import { ROUTE_NAMES, withRoot } from '@/core/routes/routeNames';
import ConfirmationDialog from '@/components/AppDialog/ConfirmationDialog';
import { getUserPortalRole } from '@/core/web-storage/webStorage.utils';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';
import { globalLocationHref, globalLocationReplace } from '@/common/utils/app.utils';

const isOnMaintenance = (location: string) => location === withRoot(ROUTE_NAMES.MAINTENANCE.ROOT);

const MaintenanceRedirectContext = createContext(undefined);
function MaintenanceRedirectProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const userRole = getUserPortalRole();
  const [isMaintenance, setIsMaintenance] = useState<undefined | boolean>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetTimer, setResetTimer] = useState(1);

  const resetTimeoutComponent = () => setResetTimer((timer) => timer + 1);
  const navigateToMaintenance = () => {
    globalLocationHref(ROUTE_NAMES.MAINTENANCE.MAIN);
    setConfirmOpen(false);
  };
  const navigateToDashboard = () => {
    if (userRole === PORTAL_ROLES.ATA) {
      globalLocationReplace(withRoot(ROUTE_NAMES.ADMINISTRATION.RATES.ROOT));
    } else {
      globalLocationReplace(withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD));
    }
  };

  async function storeMaintenanceBlobContents() {
    clearTimeout(timeoutRef.current);
    const handler = new BlobHandler(BLOB_STORAGE_CONSTANTS.STORAGE_ACCOUNT.MAINTENANCE);
    const promise = handler.getBlob(
      BLOB_STORAGE_CONSTANTS.CONTAINER.MAINTENANCE,
      BLOB_STORAGE_CONSTANTS.BLOB.MAINTENANCE,
    ) as Promise<string>;

    promise
      .then((value) => {
        const parsedValue = JSON.parse(value) as { maintenance: boolean };
        setIsMaintenance(parsedValue.maintenance);

        timeoutRef.current = setTimeout(storeMaintenanceBlobContents, 60000);
      })
      .catch(() => {
        clearTimeout(timeoutRef.current);
      });
  }

  useEffect(() => {
    if (ENVIRONMENT_NAME !== 'local') {
      void storeMaintenanceBlobContents();
    }

    return () => {
      clearTimeout(timeoutRef.current);
      setConfirmOpen(false);
      resetTimeoutComponent();
    };
  }, []);

  useEffect(() => {
    if (isMaintenance === true && !isOnMaintenance(pathname)) {
      setConfirmOpen(true);
    } else if (isMaintenance === false && isOnMaintenance(pathname)) {
      navigateToDashboard();
      setConfirmOpen(false);
      resetTimeoutComponent();
    }
  }, [isMaintenance, pathname]);

  return (
    <MaintenanceRedirectContext.Provider value={undefined}>
      <ConfirmationDialog
        open={confirmOpen}
        onYes={() => {
          navigateToMaintenance();
          setConfirmOpen(false);
        }}
        switchLabelTheme
        label="Site Maintenance Alert"
        content="We're currently performing maintenance. You'll be redirected to our maintenance page shortly."
        setVisible={setConfirmOpen}
        isCancelable={false}
        yesLabel={
          <>
            Continue (<OnTimeout onTimerEnd={navigateToMaintenance} resetKey={resetTimer} />)
          </>
        }
      />
      {children}
    </MaintenanceRedirectContext.Provider>
  );
}

function OnTimeout({
  duration = 10,
  onTimerEnd,
  resetKey,
}: {
  duration?: number;
  onTimerEnd: () => void;
  resetKey: number;
}) {
  const [timeLeft, setTimeLeft] = useState(duration);

  useEffect(() => {
    setTimeLeft(duration);
  }, [duration, resetKey]);

  useEffect(() => {
    if (timeLeft <= 0) {
      onTimerEnd();
      setTimeLeft(0);
    }

    const timerId = setInterval(() => {
      setTimeLeft((left) => left - 1);
    }, 1000);

    if (timeLeft <= 0) {
      clearInterval(timerId);
    }

    return () => clearInterval(timerId);
  }, [timeLeft]);

  return <>{timeLeft}</>;
}

export default MaintenanceRedirectProvider;
